import React from 'react'
import ProductsContainer from '../backoffice/products/ProductsContainer'
import ProductsContainerV2 from '../backoffice/products/ProductsContainerV2'
import Spinner from '../common/Spinner'
import {
  useSearchEngineInfo,
  setSearchEngineOverride,
  SEARCH_ENGINES,
} from '../../hooks/useSearchEngine'

/**
 * Decide qué buscador ve cada usuario en BackOffice → Productos.
 *
 * Envuelve, no modifica: ProductsContainer se usa tal cual, sin tocar una sola
 * línea. Cambiar de motor es un UPDATE en Configuraciones, sin desplegar.
 *
 *   legacy    la pantalla de siempre
 *   v2        sólo el motor nuevo
 *   comparar  los dos, uno debajo del otro
 *
 * Las tablas son anchas, así que en modo comparación van apiladas en vez de
 * en columnas: en paralelo obligaría a hacer scroll horizontal.
 */

const ORIGENES = {
  'override-local': 'localStorage de este navegador',
  usuario: 'fila Buscador.MotorPorUsuario',
  global: 'fila Buscador.Motor',
  defecto: 'no hay configuración; repliegue a legacy',
  cargando: 'consultando al servidor…',
}

/**
 * Qué motor está activo y por qué.
 *
 * La precedencia (localStorage > por usuario > global) es invisible, y cuando
 * la pantalla no muestra lo esperado no hay forma de saber cuál de los tres
 * mandó. Esta barra lo dice, y permite quitar el override local sin abrir la
 * consola, que es la causa más habitual de que un UPDATE "no haga nada".
 */
const BarraMotor = ({ info }) => {
  const esLocal = info.origen === 'override-local'

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        fontSize: 13,
        padding: '8px 13px',
        marginBottom: 14,
        background: esLocal ? '#f3eeda' : '#eef2f5',
        borderLeft: `3px solid ${esLocal ? '#6e5a00' : '#8b97a3'}`,
      }}
    >
      <span>
        Buscador: <strong>{info.engine}</strong>
      </span>
      <span style={{ color: '#5c6773' }}>
        origen: {ORIGENES[info.origen] || info.origen}
      </span>
      {info.idUsuario ? (
        <span style={{ color: '#5c6773' }}>usuario {info.idUsuario}</span>
      ) : null}

      {esLocal ? (
        <button
          className='user-input btn-shape'
          style={{ padding: '2px 10px', fontSize: 12 }}
          onClick={() => {
            setSearchEngineOverride(null)
            window.location.reload()
          }}
        >
          Quitar override y usar la configuración del servidor
        </button>
      ) : null}
    </div>
  )
}

const Separador = ({ titulo, nota, color }) => (
  <div
    style={{
      borderLeft: `3px solid ${color}`,
      background: '#f6f7f9',
      padding: '11px 15px',
      margin: '34px 0 18px',
    }}
  >
    <strong style={{ fontSize: 15 }}>{titulo}</strong>
    <div style={{ fontSize: 13, color: '#5c6773', marginTop: 3 }}>{nota}</div>
  </div>
)

const ProductsSearchHost = (props) => {
  const info = useSearchEngineInfo()

  // Hasta saber qué motor toca no se monta ninguno. ProductsContainer lanza
  // peticiones nada más montarse, así que montarlo "por si acaso" y luego
  // sustituirlo dispara búsquedas del motor antiguo que nadie va a leer.
  if (info.origen === 'cargando') {
    return (
      <div className='products'>
        <Spinner bloque retardado />
      </div>
    )
  }

  // En modo comparación la pantalla es una herramienta: hacen falta las
  // cabeceras para saber qué tabla es cuál, y el indicador de motor para
  // entender por qué se está viendo esto.
  if (info.engine === SEARCH_ENGINES.COMPARE) {
    return (
      <>
        <BarraMotor info={info} />

        <Separador
          titulo='Motor nuevo (v2)'
          nota='Una sola petición por página. El contador de coincidencias es el total real, no lo que cabe en pantalla.'
          color='#1b5e7e'
        />
        <ProductsContainerV2 {...props} />

        <Separador
          titulo='Motor actual'
          nota='La pantalla de siempre, sin modificar. Busca lo mismo en los dos y compara los resultados.'
          color='#8b97a3'
        />
        <ProductsContainer {...props} />
      </>
    )
  }

  // Con un solo motor, la pantalla se ve tal cual quedará en producción: sin
  // cabeceras, sin avisos y sin nada que delate que hay dos implementaciones.
  if (info.engine === SEARCH_ENGINES.V2) {
    return <ProductsContainerV2 {...props} />
  }

  return <ProductsContainer {...props} />
}

export default ProductsSearchHost
